// Keeps one parsed pdf.js document alive across a remount of its viewer.
//
// The File Hub swaps its whole turbo-frame on every chip switch (Turbo Drive is off, so
// `hub-frame#load` sets `frame.src` and the shell is replaced wholesale). That tears the
// pdf-player controller down and stands a new one up, which used to mean re-downloading and
// re-parsing the file — and the browser's HTTP cache cannot absorb it, because the viewer's
// src is an S3 URL re-signed on every render. Holding the document here instead means the
// second visit costs nothing.
//
// Deliberately a SINGLE entry: a document is the heaviest thing this app keeps in memory
// (the library's largest is 34MB), so a different key evicts the previous one immediately.

// How long a released document is held for a remount that never comes. A chip switch
// re-acquires within a frame; closing the drawer does not, and should not pin the bytes.
const IDLE_EVICT_MS = 30_000

let entry = null

// The document for `key`, loading it via `load` when this is the first ask. Returns the
// loading PROMISE rather than the resolved document so two acquires racing each other —
// what a fast double chip-click produces — share one download instead of starting two.
// `page` and `scale` are whatever the previous mount left behind, or null on a first load.
export function acquireDocument(key, load) {
  if (entry?.key === key) {
    clearTimeout(entry.evictTimer)
    entry.evictTimer = null
    entry.claims += 1
    return entry
  }

  evict()
  const loaded = { key: key, documentPromise: load(), page: null, scale: null, claims: 1, evictTimer: null }
  // A load that failed must not be handed to the next mount: nothing here retries, so one
  // bad request would leave the viewer permanently broken instead of merely slow.
  loaded.documentPromise.catch(() => {
    if (entry === loaded) entry = null
  })
  entry = loaded

  return entry
}

// The viewer is going away: remember where the user was and, once nothing else is using the
// document, start the idle countdown. Returns whether the cache kept the document — false
// means the slot has since been taken over by another file and this was the last owner, so
// the caller has to destroy it itself.
export function releaseDocument(key, { page, scale } = {}) {
  if (entry?.key !== key) return false

  clearTimeout(entry.evictTimer)
  entry.evictTimer = null
  // A viewer torn down before its first render has no position to hand over. Recording its
  // untouched defaults would come back to the next mount as if the user had deliberately
  // chosen page one at 100%, which is how the fit-to-width pass gets skipped.
  if (page) entry.page = page
  if (scale) entry.scale = scale

  entry.claims -= 1
  if (entry.claims === 0) entry.evictTimer = setTimeout(evict, IDLE_EVICT_MS)

  return true
}

function evict() {
  if (!entry) return

  clearTimeout(entry.evictTimer)
  // A viewer can still be mounted on this document — the hub renders full-page as well as in
  // the drawer, and the global upload queue can open the drawer over it on a different media.
  // Destroying it here would blank that viewer, so ownership passes to it instead: its own
  // release sees the slot has moved on and cleans up.
  if (entry.claims === 0) {
    // A load that rejected has nothing to destroy, and a timer is no place to throw.
    entry.documentPromise.then((document) => document.destroy()).catch(() => {})
  }
  entry = null
}
